import React, {useState, useEffect} from 'react'
import {useIntl} from 'react-intl'
import {KTCard, KTCardBody, Table, Loading} from '../../../components'
import {CustomCellDouble} from '../../../components/Table/columns/CustomCellDouble'
import {CustomCell} from '../../../components/Table/columns/CustomCell'
import {CustomHeader} from '../../../components/Table/columns/CustomHeader'
import {ListHeader} from '../../../components/Table/header/ListHeader'
import {ListLoading} from '../../../components/Table/ListLoading'
import {PageTitle} from '../../../core'

import {useDispatch, useSelector} from 'react-redux'
import {companyGetAll, companyExcel} from '../../../redux/slices/company.slice'
import {categoryGetAll} from '../../../redux/slices/category.slice'

import {CompanyType} from '../../../helpers'
import {
  useMemoizedColumns,
  useMemoizedStaticData,
  useMemoizedHandlers
} from '../../../utils/performanceOptimizations'

const selectCompanyData = (state) => ({
  company: state.company.company,
  loading: state.company.loading
})

const selectCategoryData = (state) => ({
  category: state.category.category
})

function List() {
  const intl = useIntl()
  const dispatch = useDispatch()

  const {company, loading} = useSelector(selectCompanyData)
  const {category} = useSelector(selectCategoryData)

  const [params, setParams] = useState()

  useEffect(() => {
    if (params) {
      dispatch(companyGetAll({params, companyTypeId: CompanyType.Supplier}))
      dispatch(categoryGetAll({SourceTypeId: 11}))
    }
  }, [params, dispatch])

  const handlers = useMemoizedHandlers({
    handleSorting: (item) => {
      setParams((prevState) => ({...prevState, sort: item && item.sort, order: item && item.order}))
    },
    handleSearch: (item) => {
      setParams((prevState) => ({...prevState, name: item}))
    },
    handlePagination: (pagination) => {
      setParams((prevState) => ({...prevState, ...pagination}))
    },
    handleExcel: () => {
      dispatch(
        companyExcel({
          params,
          companyTypeId: CompanyType.Supplier,
          fileName: intl.formatMessage({id: 'BC.SUPPLIER'}),
        })
      )
    }
  }, {
    handleSorting: [],
    handleSearch: [],
    handlePagination: [],
    handleExcel: [dispatch, params, intl]
  })

  const columnsConfig = [
    {
      Header: (props) => (
        <CustomHeader
          tableProps={props}
          handleChangeSorting={handlers.handleSorting}
          title={intl.formatMessage({id: 'COMPANY.SEARCH.NAME'})}
          className='min-w-125px'
        />
      ),
      accessor: 'name',
      Cell: ({...props}) => <CustomCellDouble item={props} double='title' />,
    },
    {
      Header: (props) => (
        <CustomHeader
          tableProps={props}
          handleChangeSorting={handlers.handleSorting}
          title={intl.formatMessage({id: 'COMPANY.SEARCH.CATEGORYNAME'})}
          className='min-w-125px'
        />
      ),
      accessor: 'categoryName',
      Cell: ({...props}) => <CustomCell item={props} />,
    },
    {
      Header: (props) => (
        <CustomHeader
          tableProps={props}
          handleChangeSorting={handlers.handleSorting}
          title={intl.formatMessage({id: 'COMPANY.SEARCH.TAXOFFICENO'})}
          className='min-w-125px'
        />
      ),
      accessor: 'taxOffice',
      Cell: ({...props}) => <CustomCellDouble item={props} double='taxNumber' />,
    },
    {
      Header: (props) => (
        <CustomHeader
          tableProps={props}
          handleChangeSorting={handlers.handleSorting}
          title={intl.formatMessage({id: 'COMPANY.SEARCH.IDENTITYNUMBER'})}
          className='min-w-125px'
        />
      ),
      accessor: 'identityNumber',
      Cell: ({...props}) => <CustomCell item={props} />,
    },
    {
      Header: (props) => (
        <CustomHeader
          tableProps={props}
          handleChangeSorting={handlers.handleSorting}
          title={intl.formatMessage({id: 'COMPANY.SEARCH.BALANCE'})}
          className='min-w-125px'
        />
      ),
      accessor: 'balance',
      Cell: ({...props}) => <CustomCellDouble item={props} double='balanceInformation' price />,
    },
    {
      Header: (props) => (
        <CustomHeader
          tableProps={props}
          handleChangeSorting={handlers.handleSorting}
          title={intl.formatMessage({id: 'LIST.LASTMODIFIEDINFO'})}
          className='min-w-125px'
        />
      ),
      accessor: 'lastModifiedBy',
      Cell: ({...props}) => <CustomCellDouble item={props} double='lastModifiedDate' />,
    },
  ]

  const columns = useMemoizedColumns(columnsConfig, [intl, handlers.handleSorting])

  const actionButtons = useMemoizedStaticData(() => [
    {
      title: 'BUTTON.EXCEL',
      onClick: handlers.handleExcel,
      icon: '/images/icons/duotune/arrows/arr044.svg',
      variant: 'light-primary',
    },
    {
      title: 'FILTER',
      icon: '/images/icons/duotune/general/gen031.svg',
      variant: 'light-primary',
      list: [
        {
          title: 'COMPANY.SEARCH.BALANCE',
          data: [
            {
              id: 'true',
              name: intl.formatMessage({id: 'FILTER.HASBALANCE.TRUE'}),
            },
            {
              id: 'false',
              name: intl.formatMessage({id: 'FILTER.HASBALANCE.FALSE'}),
            },
          ],
          name: 'hasBalance',
        },
        {
          title: 'COMPANY.SEARCH.CATEGORYNAME',
          data: category,
          name: 'categoryId',
        },
      ],
    },
  ], [intl, category, handlers.handleExcel])

  return (
    <>
      <PageTitle>{intl.formatMessage({id: 'BC.SUPPLIER'})}</PageTitle>
      <KTCard>
        <ListHeader
          search={handlers.handleSearch}
          actionButtons={actionButtons}
          onSubmit={(values) => setParams({...values})}
          pages={'SUPPLIER'}
        />
        <KTCardBody>
          {!company && <ListLoading />}
          {company && (
            <Table
              items={company}
              cols={columns}
              handlePagination={handlers.handlePagination}
              paddless
            />
          )}
        </KTCardBody>
      </KTCard>

      {loading && <Loading />}
    </>
  )
}
export {List}
